import type { MutableRefObject } from 'react';

/** The three anchors the scale is described by, and the bands around them. */
const BAND = (n: number) => (n <= 2 ? 'Not fun' : n <= 6 ? 'A bit fun' : 'Super fun');

/**
 * How good the card is, nought to nine.
 *
 * Separate from everything else on the card: the rest of the screen asks
 * whether the words are right, this asks whether the prompt is any good. A
 * translator can only answer the first; anyone who has played can answer this,
 * and it is the only signal we collect about the deck itself.
 *
 * Unrated is a real state, not a zero — the track stays grey and the readout
 * shows a dash until somebody actually says something.
 */
export default function VibeSlider({
  value, votes, average, needed, sliderRef, onChange, onPick, onCommit,
}: {
  value: number | null;
  votes: number;
  average: number;
  /** True while the card cannot be saved for want of a score. */
  needed: boolean;
  sliderRef?: MutableRefObject<HTMLInputElement | null>;
  onChange: (score: number) => void;
  /** A digit typed on the slider. When set, it replaces onChange for that key
   *  so a keypress can be the whole decision rather than a step towards one. */
  onPick?: (score: number) => void;
  /** Enter, from the slider: the last step of walking a card. */
  onCommit: () => void;
}) {
  const set = (n: number) => onChange(Math.min(9, Math.max(0, Math.round(n))));

  return (
    <div className={`vibe${value === null ? ' unset' : ''}${needed ? ' needed' : ''}`}>
      <div className="vibe-top">
        <span className="vibe-lab">Vibe</span>
        <span className="vibe-n">{value ?? '—'}</span>
        <span className="vibe-word">{value === null ? 'Not rated yet' : BAND(value)}</span>
        <span className="spacer" style={{ flex: 1 }} />
        {/* Held back until you have committed to a number of your own, so the
            room's opinion cannot become yours. */}
        {value !== null && votes > 1 && (
          <span className="vibe-avg">avg {average} of {votes}</span>
        )}
      </div>

      <input
        className="vibe-range"
        ref={sliderRef}
        type="range" min={0} max={9} step={1}
        value={value ?? 0}
        aria-label="How good is this card, 0 for not fun to 9 for super fun"
        aria-valuetext={value === null ? 'not rated yet' : `${value}, ${BAND(value)}`}
        onInput={(e) => set(e.currentTarget.valueAsNumber)}
        // A click that lands exactly on the thumb fires no input event, which
        // would leave an unrated card looking rated at zero.
        onClick={(e) => set(e.currentTarget.valueAsNumber)}
        onKeyDown={(e) => {
          if (e.key >= '0' && e.key <= '9') {
            e.preventDefault();
            const n = Number(e.key);
            if (onPick) onPick(n); else set(n);
            return;
          }
          if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
        }}
      />

      {/* Three anchors rather than ten labels: enough to fix what the ends and
          the middle mean, without pretending every step has a name. */}
      <div className="vibe-scale" aria-hidden="true">
        <span>0 · Not fun</span>
        <span>5 · It’s a bit fun</span>
        <span>9 · Super fun</span>
      </div>
    </div>
  );
}
